import React, { useEffect } from 'react';
import { notifyDownload } from '../lib/notifyDownload';
import './nimboLanding.css';

/* ── Core features ────────────────────────────────────────────── */
const FEATURES = [
  {
    icon: 'fa-th-large',
    title: 'Workspaces',
    desc: 'Create, edit, duplicate, and delete a workspace per app or environment. Each one stores host, port, user, key path, remote path, env vars, command shortcuts, and service checks.',
  },
  {
    icon: 'fa-terminal',
    title: 'Embedded SSH terminal',
    desc: 'A full interactive shell (xterm.js): colors, resize, Ctrl+C / Ctrl+D, arrow keys, and copy/paste. On connect it auto-cd&apos;s into the app path and exports the workspace env.',
  },
  {
    icon: 'fa-clone',
    title: 'Persistent connections',
    desc: 'Open several shells per workspace as tabs. Switching workspaces or tabs never drops a session — your work stays exactly where you left it.',
  },
  {
    icon: 'fa-bolt',
    title: 'Command shortcuts',
    desc: 'Saved commands run in the interactive terminal or as a one-shot output panel. Commands marked dangerous require a confirmation modal before they fire.',
  },
  {
    icon: 'fa-heartbeat',
    title: 'Service checks',
    desc: 'Per-workspace health probes (e.g. systemctl is-active nginx) compared against an expected value and shown as green / red / amber / gray badges.',
  },
  {
    icon: 'fa-sliders',
    title: 'Developer-tool UI',
    desc: 'Dark, compact, and keyboard-friendly. Collapsible sidebar, connection-status dots, and a resizable output panel — built to live on your second monitor.',
  },
];

/* ── Security ─────────────────────────────────────────────────── */
const SECURITY = [
  {
    icon: 'fa-fingerprint',
    title: 'Biometric app lock',
    desc: 'Nimbo locks behind Touch ID on launch, on system sleep or screen lock, and after an idle timeout.',
  },
  {
    icon: 'fa-key',
    title: 'Encrypted secrets at rest',
    desc: 'Workspace environment variables are encrypted on disk using the OS keychain via Electron safeStorage.',
  },
  {
    icon: 'fa-lock',
    title: 'Locked-down storage',
    desc: 'The local workspace file is written owner-only (0600) so other users on the machine can&apos;t read it.',
  },
  {
    icon: 'fa-shield',
    title: 'SSH host-key verification',
    desc: 'Trust-on-first-use: Nimbo records each server&apos;s host-key fingerprint and rejects mismatches to protect against MITM.',
  },
  {
    icon: 'fa-ban',
    title: 'No stored passwords',
    desc: 'Auth is via SSH key or ssh-agent. Raw SSH and sudo passwords are never persisted.',
  },
  {
    icon: 'fa-check-circle-o',
    title: 'Safe command execution',
    desc: 'Command, env, and path values are shell-escaped before being sent, and dangerous commands require explicit confirmation.',
  },
];

/* ── Tech / specs ─────────────────────────────────────────────── */
const STACK = [
  'Electron', 'electron-vite + Vite', 'React + TypeScript',
  'xterm.js', 'ssh2', 'zod', 'Local JSON persistence',
];

/* ── Getting started steps ────────────────────────────────────── */
const STEPS = [
  {
    n: '1',
    title: 'Download and open Nimbo',
    desc: 'Grab the macOS build below. For unsigned builds, right-click the app and choose Open the first time.',
  },
  {
    n: '2',
    title: 'Create a workspace',
    desc: 'Click + in the sidebar to add a new app or environment workspace.',
  },
  {
    n: '3',
    title: 'Fill in the details',
    desc: 'Add host, user, SSH key path, remote path, env vars, and the commands you run most.',
  },
  {
    n: '4',
    title: 'Connect',
    desc: 'Nimbo opens a shell in the right directory with your env already loaded. That&apos;s it.',
  },
];

/* ── Release notes ───────────────────────────────────────────── */
const RELEASE_NOTES = [
  {
    icon: 'fa-server',
    title: 'Reusable servers',
    desc: 'Add a workspace for another app on a box you already use by picking that server. No more retyping host, user, or SSH key. Existing workspaces upgrade automatically.',
  },
  {
    icon: 'fa-key',
    title: 'Easier SSH keys',
    desc: 'Use Browse... to pick your key file, save a passphrase-protected key passphrase securely once, or load a key into ssh-agent in one click when you use agent auto-load.',
  },
  {
    icon: 'fa-columns',
    title: 'Sidebar cleanup',
    desc: 'Workspaces are grouped by server, with a collapsible Servers list for quick edits.',
  },
  {
    icon: 'fa-magic',
    title: 'Small niceties',
    desc: 'Cleaner terminal on connect with no bootstrap flash, new tabs auto-focus, and inline help throughout the forms.',
  },
];

/* ── FAQ ──────────────────────────────────────────────────────── */
const FAQ = [
  {
    q: 'Is my data sent anywhere?',
    a: 'No. Nimbo is local-only — no cloud, accounts, or telemetry. Workspaces are stored on your machine.',
  },
  {
    q: 'Where are my passwords stored?',
    a: 'They aren&apos;t. Auth uses SSH keys or ssh-agent; no passwords are persisted. Env-var secrets are encrypted at rest via the OS keychain.',
  },
  {
    q: 'Which platforms are supported?',
    a: 'macOS today. Other platforms are on the roadmap.',
  },
  {
    q: 'Does it support passphrase-protected keys?',
    a: 'Yes. Nimbo can save a passphrase securely once for passphrase-protected keys, or you can load a key into ssh-agent and connect through the agent.',
  },
];

/* ── Worked workspace example (rendered verbatim as JSON) ─────── */
const WORKSPACE_EXAMPLE = `{
  "id": "webroster-prod",
  "name": "WebRoster Production",
  "hostname": "148.72.60.215",
  "port": 22,
  "username": "deploy",
  "identityFile": "~/.ssh/webroster_prod",
  "remotePath": "/var/www/webroster",
  "env": { "APP_CONTEXT": "webroster", "APP_ENV": "production" },
  "commands": [
    { "id": "laravel-log", "name": "Tail Laravel Log", "command": "tail -f storage/logs/laravel.log", "mode": "terminal", "isLog": true },
    { "id": "supervisor-status", "name": "Check Supervisor", "command": "supervisorctl status", "mode": "output" },
    { "id": "restart-workers", "name": "Restart Workers", "command": "sudo supervisorctl restart laravel-worker:*", "mode": "terminal", "dangerous": true, "confirm": true }
  ],
  "serviceChecks": [
    { "id": "nginx", "name": "Nginx", "command": "systemctl is-active nginx", "expected": "active" }
  ]
}`;

/* ── Head metadata (mirrors App.jsx syncHeadMetadata pattern) ──── */
const syncHeadMetadata = ({ title, description, url, image }) => {
  document.title = title;

  const metaConfigs = [
    { attr: 'name', key: 'description', content: description },
    { attr: 'property', key: 'og:title', content: title },
    { attr: 'property', key: 'og:description', content: description },
    { attr: 'property', key: 'og:type', content: 'website' },
    { attr: 'property', key: 'og:url', content: url },
    { attr: 'property', key: 'og:image', content: image },
    { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
    { attr: 'name', key: 'twitter:title', content: title },
    { attr: 'name', key: 'twitter:description', content: description },
    { attr: 'name', key: 'twitter:image', content: image },
  ];

  metaConfigs.forEach(({ attr, key, content }) => {
    let element = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attr, key);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  });

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
};

export const NimboLanding = () => {
  useEffect(() => {
    const prevTitle = document.title;
    syncHeadMetadata({
      title: 'Nimbo | App-centered SSH workspace & runbook tool by Mindware',
      description:
        'Nimbo organizes SSH access, commands, logs, and service checks around your applications instead of your servers. A local-only macOS app by Mindware.',
      url: `${window.location.origin}/nimbo`,
      image: `${window.location.origin}/img/nimbo-screenshot-placeholder.png`,
    });
    return () => { document.title = prevTitle; };
  }, []);

  return (
    <div className="nb-page">

      {/* ── Nav ── */}
      <nav className="nb-nav">
        <div className="nb-nav-inner">
          <a href="/nimbo" className="nb-nav-brand">
            <img src="img/logo-nimbo-web.png" alt="Nimbo" className="nb-nav-logo" />
            <span>Nimbo</span>
            <span className="nb-by">by Mindware</span>
          </a>
          <div className="nb-nav-links">
            <a href="#nb-problem">Problem</a>
            <a href="#nb-how">How it works</a>
            <a href="#nb-features">Features</a>
            <a href="#nb-security">Security</a>
            <a href="#nb-docs">Docs</a>
            <a href="#nb-release-notes">Release notes</a>
            <a href="#nb-download" className="nb-nav-cta">Download</a>
          </div>
          <a href="/" className="nb-back">&#8592; Mindware</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="nb-hero">
        <div className="nb-hero-bg" />
        <div className="nb-hero-grid" />
        <div className="nb-wrap nb-hero-inner">
          <div className="nb-hero-copy">
            <div className="nb-hero-badge">
              <span className="nb-badge-dot" />
              Local-only &mdash; macOS
            </div>
            <span className="nb-eyebrow">SSH Workspace &amp; Runbook Tool</span>
            <h1>
              SSH organized around
              <br />
              <span className="nb-grad">your apps, not servers.</span>
            </h1>
            <p>
              Nimbo gives every app and environment a <strong>workspace</strong> that
              knows how to connect over SSH, <code>cd</code> into the right directory,
              load app-specific env vars, and surface the commands, logs, and service
              checks you actually use. No more running the right command in the wrong place.
            </p>
            <div className="nb-hero-actions">
              <a href="#nb-download" className="nb-btn-primary">
                <i className="fa fa-apple" />&nbsp; Download for macOS
              </a>
              <a href="#nb-how" className="nb-btn-ghost">How it works</a>
            </div>
            <div className="nb-hero-meta">
              <span><i className="fa fa-shield" /> Biometric lock</span>
              <span><i className="fa fa-cloud" /> No cloud</span>
              <span><i className="fa fa-eye-slash" /> No telemetry</span>
            </div>
          </div>

          <div className="nb-hero-visual">
            {/* TODO: Replace this placeholder with a real Nimbo product screenshot.
                Drop the final PNG at public/img/nimbo-screenshot-placeholder.png
                (or rename and update the src + OG image reference). */}
            <div className="nb-window">
              <div className="nb-window-bar">
                <span className="nb-dot nb-dot--r" />
                <span className="nb-dot nb-dot--y" />
                <span className="nb-dot nb-dot--g" />
                <span className="nb-window-title">Nimbo &mdash; WebRoster Production</span>
              </div>
              <img
                src="img/nimbo-screenshot-placeholder.png"
                alt="Nimbo workspace with an embedded SSH terminal, command shortcuts, and service-check badges"
                className="nb-window-shot"
              />
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* ── The problem ── */}
        <section id="nb-problem" className="nb-section nb-problem-section">
          <div className="nb-wrap">
            <div className="nb-section-head">
              <span className="nb-label">The Problem</span>
              <h2>One server, many apps, endless context-switching.</h2>
              <p>
                Developers who run several apps on one or more shared servers constantly
                juggle SSH commands, app paths, keys, logs, queue workers, containers,
                services, and deploy commands. That&apos;s friction &mdash; and risk.
              </p>
            </div>
            <div className="nb-problem-grid">
              <div className="nb-problem-card">
                <i className="fa fa-random" />
                <h3>Scattered context</h3>
                <p>Hosts, key paths, and remote directories live in your head, a notes file, and three terminal tabs.</p>
              </div>
              <div className="nb-problem-card">
                <i className="fa fa-exclamation-triangle" />
                <h3>Costly mistakes</h3>
                <p>Running the right command in the wrong app or environment is a single typo away.</p>
              </div>
              <div className="nb-problem-card">
                <i className="fa fa-repeat" />
                <h3>Repetition</h3>
                <p>The same connect → cd → export → tail dance, every single time, for every single app.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section id="nb-how" className="nb-section nb-how-section">
          <div className="nb-wrap">
            <div className="nb-section-head">
              <span className="nb-label">How It Works</span>
              <h2>A workspace per app. It does the setup for you.</h2>
              <p>
                Instead of organizing around servers, Nimbo organizes around apps. Each
                workspace knows how to connect and what to do once it&apos;s there.
              </p>
            </div>
            <div className="nb-flow">
              <div className="nb-flow-item">
                <i className="fa fa-plug" />
                <span>Connect</span>
                <small>Opens SSH using your key or ssh-agent</small>
              </div>
              <div className="nb-flow-arrow">&#8594;</div>
              <div className="nb-flow-item">
                <i className="fa fa-folder-open-o" />
                <span>cd &amp; export</span>
                <small>Lands in the app path with env loaded</small>
              </div>
              <div className="nb-flow-arrow">&#8594;</div>
              <div className="nb-flow-item nb-flow-item--out">
                <i className="fa fa-bolt" />
                <span>Quick actions</span>
                <small>Shell, tail logs, run commands, check services</small>
              </div>
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section id="nb-features" className="nb-section nb-features-section">
          <div className="nb-wrap">
            <div className="nb-section-head">
              <span className="nb-label">Features</span>
              <h2>Everything you run, where you run it</h2>
              <p>Built for the way developers actually work on shared servers.</p>
            </div>
            <div className="nb-grid">
              {FEATURES.map((f) => (
                <article key={f.title} className="nb-feat-card">
                  <i className={`fa ${f.icon} nb-feat-icon`} aria-hidden="true" />
                  <h3>{f.title}</h3>
                  <p dangerouslySetInnerHTML={{ __html: f.desc }} />
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Security ── */}
        <section id="nb-security" className="nb-section nb-security-section">
          <div className="nb-wrap">
            <div className="nb-section-head">
              <span className="nb-label nb-label--lock"><i className="fa fa-shield" /> Security</span>
              <h2>Security is the whole point</h2>
              <p>
                Nimbo holds the keys to your servers, so it&apos;s built to protect them.
                Everything stays on your machine, locked down by default.
              </p>
            </div>
            <div className="nb-grid">
              {SECURITY.map((s) => (
                <article key={s.title} className="nb-sec-card">
                  <div className="nb-sec-icon"><i className={`fa ${s.icon}`} aria-hidden="true" /></div>
                  <h3>{s.title}</h3>
                  <p dangerouslySetInnerHTML={{ __html: s.desc }} />
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Tech / specs ── */}
        <section id="nb-specs" className="nb-section nb-specs-section">
          <div className="nb-wrap nb-specs-inner">
            <div className="nb-specs-col">
              <span className="nb-label">Tech &amp; Specs</span>
              <h2>Built local-first</h2>
              <p>
                Nimbo is a desktop app, not a service. There&apos;s no backend to trust and
                nothing to sign in to — your workspaces never leave your machine.
              </p>
              <dl className="nb-specs-list">
                <div><dt>Platform</dt><dd>macOS desktop app (primary target)</dd></div>
                <div><dt>Requirements</dt><dd>An SSH server reachable with key-based auth</dd></div>
                <div><dt>Stance</dt><dd>Local-only — no cloud, no accounts, no telemetry</dd></div>
                <div><dt>Storage</dt><dd>Local JSON in the app&apos;s userData directory (owner-only)</dd></div>
              </dl>
            </div>
            <div className="nb-specs-col">
              <div className="nb-stack-card">
                <h3>Tech stack</h3>
                <div className="nb-stack-tags">
                  {STACK.map((t) => (
                    <span key={t} className="nb-tag">{t}</span>
                  ))}
                </div>
                <p className="nb-stack-note">
                  Roadmap items such as additional platforms are planned, not shipped.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Getting started / docs ── */}
        <section id="nb-docs" className="nb-section nb-docs-section">
          <div className="nb-wrap">
            <div className="nb-section-head">
              <span className="nb-label">Getting Started</span>
              <h2>From download to connected in four steps</h2>
              <p>A quick docs intro. Configure a workspace once and reconnect in a click.</p>
            </div>
            <div className="nb-steps">
              {STEPS.map((s) => (
                <div key={s.n} className="nb-step">
                  <span className="nb-step-num">{s.n}</span>
                  <div>
                    <h3>{s.title}</h3>
                    <p dangerouslySetInnerHTML={{ __html: s.desc }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="nb-code-block">
              <div className="nb-code-head">
                <span><i className="fa fa-file-code-o" /> Example workspace</span>
                <span className="nb-code-lang">JSON</span>
              </div>
              <pre className="nb-code"><code>{WORKSPACE_EXAMPLE}</code></pre>
            </div>
          </div>
        </section>

        {/* ── Release notes ── */}
        <section id="nb-release-notes" className="nb-section nb-release-section">
          <div className="nb-wrap">
            <div className="nb-section-head">
              <span className="nb-label">Release Notes</span>
              <h2>Nimbo 1.4.0</h2>
              <p>
                Servers are now reusable, SSH keys are easier to manage, and the
                workspace sidebar is cleaner.
              </p>
            </div>
            <div className="nb-release-grid">
              {RELEASE_NOTES.map((item) => (
                <article key={item.title} className="nb-release-card">
                  <div className="nb-release-icon">
                    <i className={`fa ${item.icon}`} aria-hidden="true" />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="nb-faq" className="nb-section nb-faq-section">
          <div className="nb-wrap nb-faq-wrap">
            <div className="nb-section-head">
              <span className="nb-label">FAQ</span>
              <h2>Common questions</h2>
            </div>
            <div className="nb-faq-list">
              {FAQ.map((item) => (
                <details key={item.q} className="nb-faq-item">
                  <summary>{item.q}</summary>
                  <p dangerouslySetInnerHTML={{ __html: item.a }} />
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── Footer CTA ── */}
        <section id="nb-download" className="nb-section nb-cta-section">
          <div className="nb-wrap nb-cta-inner">
            <span className="nb-label">Download</span>
            <h2>Put your servers in order.</h2>
            <p>
              Nimbo is a local-only macOS app. Download it, create a workspace, and
              connect in seconds — your keys and secrets never leave your machine.
            </p>
            <div className="nb-download-actions">
              <a
                href="/release/Nimbo-1.4.0-arm64.dmg"
                className="nb-btn-primary nb-btn-lg"
                download
                onClick={() => notifyDownload('Apple Silicon (arm64)')}
              >
                <i className="fa fa-apple" />&nbsp; Apple Silicon
                <span className="nb-btn-meta">.dmg &middot; arm64</span>
              </a>
              <a
                href="/release/Nimbo-1.4.0.dmg"
                className="nb-btn-ghost nb-btn-lg"
                download
                onClick={() => notifyDownload('Intel (x64)')}
              >
                <i className="fa fa-apple" />&nbsp; Intel
                <span className="nb-btn-meta">.dmg &middot; x64</span>
              </a>
            </div>
            <p className="nb-download-note">
              Version 1.4.0 &middot; macOS 11+. Not sure which build? Macs from
              2020 onward are mostly Apple Silicon (M1/M2/M3). Unsigned build &mdash;
              on first launch, right-click the app and choose <strong>Open</strong>.
            </p>
            <div className="nb-cta-sub">
              <a href="mailto:contact@mindware.com.mx">Questions? contact@mindware.com.mx</a>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="nb-footer">
        <div className="nb-wrap nb-footer-inner">
          <span>Nimbo by <a href="/">Mindware</a></span>
          <div className="nb-footer-links">
            <a href="/#products">Other products</a>
            <a href="/">&#8592; Back to Mindware</a>
          </div>
        </div>
      </footer>

    </div>
  );
};
